/**
 * @file 登录页（Login）
 *
 * 该文件定义了 CRM 系统的登录页组件 `CrmLoginPage`。
 * 主要功能包括：
 * - 左侧品牌展示区（60% 宽度，含 Canvas 粒子动画背景、品牌 Logo 和标语）
 * - 右侧登录表单区（40% 宽度，含用户名/密码输入、Friendly Captcha 验证）
 * - 表单实时校验（输入即清空错误、提交时校验非空）
 * - Friendly Captcha（PoW 方案）人机验证集成
 * - 密码可见性切换
 * - 加载状态显示（旋转图标 + 禁用按钮）
 * - 已登录用户自动跳转至客户列表页
 *
 * 数据来源：`authStore.login()` 进行认证，`router` 进行路由导航
 *
 * 设计决策：
 * - Canvas 粒子动画使用原生 Canvas API（非第三方库），支持鼠标排斥效果和粒子间连线
 * - 使用 ResizeObserver 监听容器尺寸变化，自动重初始化 Canvas
 * - Friendly Captcha 容器提前创建到 Light DOM（slot），确保 SDK 能在组件渲染后找到挂载点
 * - 移动端隐藏左侧品牌区，仅显示登录表单
 * - 表单使用 @state 驱动实时校验，提交后标记 touched 状态
 */

import { css, html } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { authStore } from '../store.js';
import { router } from '../router.js';
import { toast } from '../components/toast.js';
import { BaseElement } from '../base-element.js';

/** Friendly Captcha 站点密钥，从环境变量读取，默认使用内置测试 key */
const FRC_SITEKEY = import.meta.env.VITE_FRC_SITEKEY || 'FCMOKMRFRNCRFLT4';
/** Friendly Captcha SDK 脚本路径（本地 vendor 目录） */
const FRC_SCRIPT = '/vendor/frc-widget.min.js';

/**
 * 登录页组件
 *
 * 继承 `BaseElement`，注册为 `<crm-login-page>` 自定义元素。
 * 提供用户登录功能，包含品牌展示区、Canvas 粒子动画、登录表单和人机验证。
 *
 * 状态字段：
 * - `leftImageUrl` — 左侧品牌区背景图 URL（可选，为空时使用渐变背景）
 * - `username` / `password` — 表单输入值
 * - `usernameError` / `passwordError` — 字段校验错误消息
 * - `isLoading` — 登录请求中状态
 * - `touched` — 表单是否已提交过（控制输入时是否清空错误）
 * - `captchaSolution` — Friendly Captcha 验证结果 token
 *
 * Canvas 动画相关：
 * - `canvasEl` — Canvas DOM 引用
 * - `animFrameId` — requestAnimationFrame ID
 * - `particles` — 粒子数组，每个粒子含位置、速度、半径、颜色
 * - `mouseX` / `mouseY` — 鼠标坐标（用于粒子排斥效果）
 */
@customElement('crm-login-page')
export class CrmLoginPage extends BaseElement {
  /** 左侧品牌区背景图 URL，为空时使用渐变背景 */
  @property({ type: String })
  leftImageUrl = '';

  /** 用户名输入值 */
  @state()
  private username = '';

  /** 密码输入值 */
  @state()
  private password = '';

  /** 用户名字段校验错误消息，为空表示无错误 */
  @state()
  private usernameError = '';

  /** 密码字段校验错误消息，为空表示无错误 */
  @state()
  private passwordError = '';

  /** 登录请求进行中标志，控制按钮禁用和加载动画 */
  @state()
  private isLoading = false;

  /** 表单是否已提交过，提交后输入时即时清空对应字段的错误 */
  private touched = false;

  /** Canvas DOM 引用，通过 @query 装饰器获取 */
  @query('#login-canvas')
  private canvasEl!: HTMLCanvasElement;

  /** requestAnimationFrame 返回的 ID，用于取消动画 */
  private animFrameId = 0;
  /** 粒子数组，每个粒子包含位置(x,y)、速度(vx,vy)、半径(r)和颜色(color) */
  private particles: Array<{x: number; y: number; vx: number; vy: number; r: number; color: string}> = [];
  /** 鼠标 X 坐标，初始 -1000 使粒子不受影响 */
  private mouseX = -1000;
  /** 鼠标 Y 坐标，初始 -1000 使粒子不受影响 */
  private mouseY = -1000;

  /** 粒子颜色调色板，包含不同透明度的黑色和品牌色 */
  private particleColors = [
    'rgba(0,0,0,0.4)',
    'rgba(13,116,206,0.38)',
    'rgba(71,194,255,0.3)',
    'rgba(129,69,181,0.32)',
    'rgba(96,100,108,0.32)',
  ];
  /** ResizeObserver 实例，监听容器尺寸变化时重新初始化 Canvas */
  private resizeObserver?: ResizeObserver;

  /** 组件样式（空，依赖全局 Tailwind 样式） */
  static styles = css``;

  /**
   * 组件挂载到 DOM 后的生命周期回调
   *
   * 1. 检查登录状态：已登录则直接跳转至客户列表页
   * 2. 提前创建 Friendly Captcha 容器到 Light DOM（slot="frc"），
   *    确保 SDK 脚本加载后能找到挂载点
   */
  connectedCallback() {
    super.connectedCallback();
    // 已登录用户直接跳转，不展示登录页
    if (router.isLoggedIn) {
      router.navigate('customers');
      return;
    }
    // Friendly Captcha 容器提前创建到 Light DOM，确保 SDK 加载时能找到
    if (!this.querySelector('[slot="frc"]')) {
      const el = document.createElement('div');
      el.setAttribute('slot', 'frc');
      this.appendChild(el);
    }
  }

  /** Friendly Captcha 验证结果 token，登录时一并发送至后端 */
  private captchaSolution = '';

  /**
   * 首次渲染完成后的生命周期回调
   *
   * 启动 Canvas 粒子动画和初始化 Friendly Captcha 组件。
   */
  protected firstUpdated() {
    this.startCanvas();
    this.setupFriendlyCaptcha();
  }

  /**
   * 组件从 DOM 移除时的生命周期回调
   *
   * 调用父类方法后停止 Canvas 动画（取消 requestAnimationFrame、断开 ResizeObserver、移除事件监听）。
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopCanvas();
  }

  /**
   * 初始化 Canvas 粒子系统
   *
   * 根据容器尺寸设置 Canvas 分辨率（考虑 devicePixelRatio 实现高清渲染），
   * 生成粒子数组，粒子数量与画布面积成正比（上限 60 个）。
   */
  private initCanvas() {
    const canvas = this.canvasEl;
    if (!canvas) return;
    const rect = canvas.parentElement!.getBoundingClientRect();
    // 高清渲染：实际像素 = CSS 像素 × devicePixelRatio
    canvas.width = rect.width * devicePixelRatio;
    canvas.height = rect.height * devicePixelRatio;
    canvas.style.width = rect.width + 'px';
    canvas.style.height = rect.height + 'px';

    const w = canvas.width;
    const h = canvas.height;
    // 粒子数量与面积成正比，上限 60 个以保证性能
    const count = Math.min(60, Math.floor((w * h) / 15000));
    this.particles = Array.from({ length: count }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.6,
      vy: (Math.random() - 0.5) * 0.6,
      r: Math.random() * 1.5 + 0.8,
      color: this.particleColors[Math.floor(Math.random() * this.particleColors.length)],
    }));
  }

  /**
   * Canvas 动画帧渲染函数
   *
   * 每帧执行以下操作：
   * 1. 清空画布
   * 2. 遍历所有粒子：更新位置、边界反弹、鼠标排斥
   * 3. 绘制粒子辉光效果（径向渐变）
   * 4. 绘制粒子实心圆
   * 5. 绘制相邻粒子之间的连线（距离小于 100px 时）
   * 6. 通过 requestAnimationFrame 递归调用下一帧
   */
  private tickAnimation() {
    const canvas = this.canvasEl;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    for (let i = 0; i < this.particles.length; i++) {
      const p = this.particles[i];
      // 更新粒子位置
      p.x += p.vx;
      p.y += p.vy;
      // 边界反弹
      if (p.x < 0 || p.x > w) p.vx *= -1;
      if (p.y < 0 || p.y > h) p.vy *= -1;

      // 鼠标排斥：鼠标 80px 范围内的粒子被推开
      const mdx = p.x - this.mouseX;
      const mdy = p.y - this.mouseY;
      const mdist = Math.sqrt(mdx * mdx + mdy * mdy);
      if (mdist < 80 && mdist > 0) {
        p.x += (mdx / mdist) * 1.2;
        p.y += (mdy / mdist) * 1.2;
      }

      // 绘制粒子辉光效果（径向渐变）
      const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
      glow.addColorStop(0, p.color);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2);
      ctx.fillStyle = glow;
      ctx.fill();

      // 绘制粒子实心圆
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.fill();

      // 绘制相邻粒子之间的连线（距离小于 100px）
      for (let j = i + 1; j < this.particles.length; j++) {
        const q = this.particles[j];
        const dx = p.x - q.x;
        const dy = p.y - q.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 100) {
          // 透明度随距离递减
          ctx.beginPath();
          ctx.moveTo(p.x, p.y);
          ctx.lineTo(q.x, q.y);
          ctx.strokeStyle = `rgba(0,0,0,${0.30 * (1 - dist / 100)})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      }
    }

    // 递归调用下一帧
    this.animFrameId = requestAnimationFrame(() => this.tickAnimation());
  }

  /**
   * 鼠标移动事件处理器（箭头函数，保持 this 绑定）
   *
   * 将客户端坐标转换为 Canvas 内坐标（考虑 devicePixelRatio）。
   * @param e - 鼠标事件对象
   */
  private _handleMouseMove = (e: MouseEvent) => {
    const rect = this.canvasEl.parentElement!.getBoundingClientRect();
    this.mouseX = (e.clientX - rect.left) * devicePixelRatio;
    this.mouseY = (e.clientY - rect.top) * devicePixelRatio;
  };

  /**
   * 鼠标离开事件处理器
   *
   * 将鼠标坐标重置为远离画布的位置，使粒子不再受排斥。
   */
  private _handleMouseLeave = () => {
    this.mouseX = -1000;
    this.mouseY = -1000;
  };

  /**
   * 启动 Canvas 动画
   *
   * 初始化粒子系统、启动动画循环、注册 ResizeObserver 和鼠标事件监听。
   */
  private startCanvas() {
    this.initCanvas();
    this.tickAnimation();
    // 监听容器尺寸变化，自动重初始化 Canvas（响应式适配）
    this.resizeObserver = new ResizeObserver(() => this.initCanvas());
    this.resizeObserver.observe(this.canvasEl.parentElement!);
    // 注册鼠标事件用于粒子排斥效果
    this.canvasEl.parentElement!.addEventListener('mousemove', this._handleMouseMove);
    this.canvasEl.parentElement!.addEventListener('mouseleave', this._handleMouseLeave);
  }

  /**
   * 停止 Canvas 动画
   *
   * 取消 requestAnimationFrame、断开 ResizeObserver、移除鼠标事件监听。
   * 在 disconnectedCallback 中调用以防止内存泄漏。
   */
  private stopCanvas() {
    cancelAnimationFrame(this.animFrameId);
    this.resizeObserver?.disconnect();
    this.canvasEl?.parentElement?.removeEventListener('mousemove', this._handleMouseMove);
    this.canvasEl?.parentElement?.removeEventListener('mouseleave', this._handleMouseLeave);
  }

  /**
   * 清空所有字段校验错误
   */
  private clearErrors() {
    this.usernameError = '';
    this.passwordError = '';
  }

  /**
   * 输入事件处理器
   *
   * 更新对应字段的值。如果表单已提交过（touched=true），
   * 则在输入时即时清空该字段的错误消息。
   *
   * @param field - 字段标识：'username' 或 'password'
   * @param value - 输入的新值
   */
  private handleInput(field: string, value: string) {
    if (field === 'username') this.username = value;
    else this.password = value;
    // 有校验错误时，输入即清空错误
    if (this.touched) {
      if (field === 'username') this.usernameError = '';
      else this.passwordError = '';
    }
  }

  /**
   * 登录表单提交处理器
   *
   * 1. 清空已有错误、标记 touched 状态
   * 2. 校验用户名和密码非空
   * 3. 调用 `authStore.login()` 发送登录请求（含 captcha token）
   * 4. 成功后设置登录状态（路由会自动跳转）
   * 5. 失败时显示错误 toast，重置 captcha 并触发 frc:reset 事件重新生成验证
   *
   * @param e - 表单提交事件
   * @returns 无返回值（Promise<void>）
   */
  private async handleSubmit(e: Event) {
    e.preventDefault();
    this.clearErrors();
    this.touched = true;

    const username = this.username.trim();
    const password = this.password.trim();

    // 非空校验
    let hasError = false;
    if (!username) {
      this.usernameError = '请输入用户名';
      hasError = true;
    }
    if (!password) {
      this.passwordError = '请输入密码';
      hasError = true;
    }
    if (hasError) return;

    this.isLoading = true;

    try {
      await authStore.login(username, password, this.captchaSolution);
      router.setLoginState(true);
    } catch (err: any) {
      toast.error(err.message || '登录失败');
      // 重置 captcha，使其重新生成 PoW
      this.captchaSolution = '';
      // Friendly Captcha PoW 过期时重置 widget
      const el = this.querySelector('[slot="frc"]') as HTMLElement | null;
      if (el) try { el.dispatchEvent(new Event('frc:reset')); } catch { /* */ }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * 表单是否有效（用户名和密码均非空）
   * @returns true 表示表单可提交
   */
  private get isFormValid() {
    return this.username.trim().length > 0 && this.password.trim().length > 0;
  }

  /** Friendly Captcha widget 实例引用 */
  private frcWidget: any = null;

  /**
   * 初始化 Friendly Captcha 组件
   *
   * 在 slot="frc" 容器中创建验证 widget，监听以下事件：
   * - `frc:widget.complete` — 验证完成，获取 solution token
   * - `frc:widget.expire` — 验证过期，清空 solution
   *
   * 如果 SDK 脚本尚未加载，动态插入 script 标签并监听 onload 回调。
   */
  private setupFriendlyCaptcha() {
    const el = this.querySelector('[slot="frc"]') as HTMLElement | null;
    if (!el) return;

    const initWidget = () => {
      const sdk = (window as any).frcaptcha;
      if (!sdk) return;
      this.frcWidget = sdk.createWidget({
        element: el,
        sitekey: FRC_SITEKEY,
      });
      // 验证完成事件：获取 PoW solution token
      this.frcWidget.addEventListener('frc:widget.complete', (e: CustomEvent) => {
        this.captchaSolution = e.detail?.response || '';
      });
      // 验证过期事件：清空 solution，用户需重新验证
      this.frcWidget.addEventListener('frc:widget.expire', () => {
        this.captchaSolution = '';
      });
    };

    // 动态加载 SDK 脚本（仅在尚未加载时）
    if (!document.querySelector('script[src*="frc-widget"]')) {
      const script = document.createElement('script');
      script.src = FRC_SCRIPT;
      script.async = true;
      script.defer = true;
      script.onload = initWidget;
      document.head.appendChild(script);
    } else {
      // SDK 已加载，直接初始化
      initWidget();
    }
  }

  /**
   * 渲染表单字段
   *
   * 通用化的输入字段渲染方法，支持文本和密码类型。
   * 根据是否有错误消息动态切换边框颜色，并在字段下方显示错误提示。
   *
   * @param label - 字段标签文本
   * @param id - 字段 ID（用于 label 的 for 属性和 handleInput 的 field 参数）
   * @param type - input 类型（text / password / email 等）
   * @param value - 字段当前值
   * @param placeholder - 占位提示文本
   * @param errorMsg - 校验错误消息，为空时不渲染错误提示
   * @param autofocus - 是否自动聚焦
   * @returns Lit TemplateResult
   */
  private renderField(label: string, id: string, type: string, value: string, placeholder: string, errorMsg: string, autofocus = false) {
    const hasError = !!errorMsg;
    return html`
      <div class="mb-5">
        <label for="${id}" class="block mb-2 text-sm font-medium text-text">${label}</label>
        <input
          id="${id}"
          type="${type}"
          class="w-full px-3 py-2.5 border ${hasError ? 'border-error' : 'border-border'} rounded-md bg-transparent text-text text-sm focus:border-primary focus:ring-0 focus:outline-none transition-all placeholder:text-text-muted"
          .value=${value}
          @input=${(e: InputEvent) => this.handleInput(id, (e.target as HTMLInputElement).value)}
          placeholder="${placeholder}"
          ?autofocus=${autofocus}
          autocomplete="${id === 'username' ? 'username' : 'current-password'}"
        />
        ${errorMsg ? html`<p class="m-0 mt-1.5 text-xs text-error">${errorMsg}</p>` : ''}
      </div>
    `;
  }

  /**
   * 渲染登录页面
   *
   * 页面布局分为两部分：
   * - 左侧（60%）：品牌展示区，含 Canvas 粒子动画背景、品牌 Logo 和标语
   *   - 如果设置了 leftImageUrl 则显示背景图，否则使用渐变背景
   *   - 移动端隐藏（md:flex）
   * - 右侧（40%）：登录表单区
   *   - 移动端显示简化 Logo
   *   - 桌面端显示标题和副标题
   *   - 表单卡片含用户名、密码、Friendly Captcha 和登录按钮
   *
   * 提交按钮在表单无效或加载中时禁用，加载中时显示旋转动画。
   *
   * @returns Lit TemplateResult
   */
  render() {
    // 按钮禁用条件：表单无效或正在加载
    const disabled = !this.isFormValid || this.isLoading;

    return html`
      <div class="flex h-full w-full">
        <!-- Left 60% — brand panel -->
        <div class="hidden md:flex w-[60%] h-full ${this.leftImageUrl ? '' : 'bg-gradient-to-br from-primary-light via-bg to-white'} items-center justify-center relative overflow-hidden">
          <canvas id="login-canvas" class="absolute inset-0 w-full h-full"></canvas>
          ${this.leftImageUrl
            ? html`<img src="${this.leftImageUrl}" class="absolute inset-0 w-full h-full object-cover" alt="" />`
            : ''}
          <div class="relative ${this.leftImageUrl ? 'z-10' : ''} text-center px-12">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6 shadow-lg shadow-primary/20">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-8 h-8"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <h1 class="m-0 font-bold text-3xl text-text tracking-tight leading-tight">RedSun CRM</h1>
            <p class="mt-3 text-base text-text-secondary leading-relaxed max-w-sm">高效管理客户关系，驱动业务增长</p>
          </div>
        </div>

        <!-- Right 40% — login form -->
        <div class="flex flex-col items-center justify-center w-full md:w-[40%] h-full px-8 bg-white">
          <div class="w-full max-w-xs flex flex-col flex-1 justify-center">
            <!-- Mobile-only logo -->
            <div class="md:hidden text-center mb-8">
              <div class="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary text-white mb-4 shadow-lg shadow-primary/20">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <h2 class="m-0 font-semibold text-2xl text-text">RedSun CRM</h2>
            </div>

            <!-- Desktop heading -->
            <div class="hidden md:block mb-8">
              <div class="flex items-center gap-2 mb-5">
                <div class="w-8 h-1 rounded-full bg-gradient-to-r from-primary to-primary/30"></div>
                <div class="w-1.5 h-1.5 rounded-full bg-primary/30"></div>
              </div>
              <h2 class="m-0 font-semibold text-2xl text-text">欢迎回来</h2>
              <p class="mt-2 text-sm text-text-secondary">登录到您的 CRM 账户</p>
            </div>

            <!-- Form card -->
            <div class="bg-white rounded-md p-6 shadow-md border border-border/40 relative overflow-hidden">
            <div class="absolute left-0 top-6 bottom-6 w-[3px] rounded-r-full bg-gradient-to-b from-primary/50 via-primary/20 to-transparent"></div>
            <div class="ml-1">
            <form @submit=${this.handleSubmit}>
              ${this.renderField('用户名', 'username', 'text', this.username, '请输入用户名', this.usernameError, true)}
              ${this.renderField('密码', 'password', 'password', this.password, '请输入密码', this.passwordError)}

              <div class="mb-5 flex justify-center">
                <slot name="frc"></slot>
              </div>

              <button
                type="submit"
                class="w-full h-[44px] rounded-md transition-all duration-200 text-sm font-medium flex items-center justify-center mt-2 ${disabled
                  ? 'bg-text-muted text-text-secondary cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-hover text-white shadow-sm shadow-primary/20 cursor-pointer hover:shadow-md hover:shadow-primary/25'}"
                ?disabled=${disabled}
              >
                ${this.isLoading
                  ? html`<svg class="animate-spin w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg><span>登录中...</span>`
                  : '登 录'}
              </button>
            </form>
            </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
